// @flow
import React, { useCallback, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape, useIntl,
} from 'react-intl';
import withRouter from 'speed-js-react/src/router/withRouter';
import PageMainSelector from 'esm-web-pro/components/pages/PageMainSelector';
import MapObjectsPage from 'esm-web-pro/containers/pages/projects/mapobjects/MapObjectsPage';
import CheckboxContextProvider from 'esm-web-pro/containers/pages/projects/mapobjects/checkbox/CheckboxContext';
import CheckboxHeader from 'esm-web-pro/containers/pages/projects/mapobjects/checkbox/CheckboxHeader';

import CheckboxColumn from 'esm-web-pro/containers/pages/projects/mapobjects/checkbox/CheckboxColumn';
import ActionsColumn from 'speed-js-react-web/src/components/grommet2/tables/columns/Action/ActionsColumn';
import QuickViewIcon from 'esm-web-pro/components/icons/QuickViewIcon';

import { User as ClientIcon } from 'grommet-icons';
import GisPointsAttributes from
    'esm-web-pro/containers/pages/projects/mapobjects/gispoints/sidepanel/GisPointsAttributes';
import GisLinesAttributes from
    'esm-web-pro/containers/pages/projects/mapobjects/gislines/sidepanel/GisLinesAttributes';

import MapObjectsSidePanel from 'esm-web-pro/containers/pages/projects/mapobjects/MapObjectsSidePanel';

export const GISPOINTS_SIDEPANEL_KEY = 'GISPOINTS_SIDEPANEL_KEY';
export const GISLINES_SIDEPANEL_KEY = 'GISLINES_SIDEPANEL_KEY';

const messages = defineMessages({
    unlinkObjects: {
        id: 'unlink-gis-confirm',
        defaultMessage: 'Are you sure you want to unlink the objects?',
    },
    actionFailed: {
        id: 'response-error',
        defaultMessage: 'Error occurred, please try again later.',
    },
    line: {
        id: 'project-lines',
        defaultMessage: 'Line.',
    },
    point: {
        id: 'project-points',
        defaultMessage: 'Point.',
    },
});

const excludeFields = {
    gisPoints: ['alias', 'coordinates', 'description', 'project', 'map'],
    gisLines: [],
};

const resourceName = {
    gisPoints: 'projectGisNested',
    gisLines: 'projectGisLinesNested',
};

const permissions = {
    gisPoints: {
        actions: {
            change: ['networks.change_pointlayer1_1'],
            delete: ['networks.delete_pointlayer1_1'],
        },
        view: ['networks.view_pointlayer1_1'],
    },
    gisLines: {
        actions: {
            change: ['networks.change_line'],
            delete: ['networks.delete_line'],
        },
        view: ['networks.view_line'],
    },
};

const tableId = {
    gisPoints: 'project-gispoints',
    gisLines: 'project-gislines',
};

// Get object type for different resources
const getObjectType = (object) => (object.type ? object.type : object.path.type);

// Props to pass to side panels
const gisPointsSidePanelExtraProps = {
    titleLabel: 'GIS Points',
    text: <FormattedMessage id="point" defaultMessage="Point" />,
    icon: <ClientIcon size="medium" style={{ display: 'block', margin: '10px' }} />,
    attributes: (object) => <GisPointsAttributes point={object} />,
    editorKey: GISPOINTS_SIDEPANEL_KEY,
    resourceName: 'projectGisNested',
    excludeFields: [
        'gis_point', 'infrastructure_provider', 'type', 'provider_ref', 'export', 'property_owner', 'map', 'project',
    ],
    permissions: permissions.gisPoints.actions,
    getObjectType,
};

const gisLinesSidePanelExtraProps = {
    titleLabel: 'GIS Lines',
    text: <FormattedMessage id="line" defaultMessage="Line" />,
    icon: <ClientIcon size="medium" style={{ display: 'block', margin: '10px' }} />,
    attributes: (object) => <GisLinesAttributes line={object} />,
    editorKey: GISLINES_SIDEPANEL_KEY,
    resourceName: 'projectGisLinesNested',
    excludeFields: ['map', 'infrastructure_provider', 'path', 'project', 'building_status', 'uuid'],
    permissions: permissions.gisLines.actions,
    getObjectType,
};

// Columns props to pass to ListPage component
const columnsGisPoints = (onSelectAll, selectAll, project, panelId, reloadData) => [
    {
        Header: <CheckboxHeader selectAll={selectAll} onClick={() => onSelectAll(!selectAll)} />,
        Cell: (cellProps) => <CheckboxColumn {...cellProps} />,
        minWidth: 120,
        sortable: false,
    },
    {
        Header: <FormattedMessage id="name" defaultMessage="Name" />,
        accessor: 'to_str',
        minWidth: 120,
    },
    {
        Header: <FormattedMessage id="building-status" defaultMessage="Building status" />,
        accessor: 'building_status',
        minWidth: 120,
    },
    {
        Header: <FormattedMessage id="stage" defaultMessage="Stage" />,
        accessor: 'project_stage.name',
        minWidth: 120,
    },
    {
        actions: [
            {
                label: <FormattedMessage
                    id="projmanag-counterparties-quickview"
                    defaultMessage="Quick View"
                />,
                type: 'quickView',
                accessor: 'uuid',
                resourceArguments: [project.uuid],
                component: MapObjectsSidePanel,
                extraProps: gisPointsSidePanelExtraProps,
                resourceName: 'projectGisNested',
                icon: <QuickViewIcon />,
                to: '/{id}/quick_view',
                getTitle: (obj) => obj.ref || obj.sequence,
                panelId,
                reload: reloadData,
            },
        ],
        Cell: ActionsColumn,
        minWidth: 80,
    },
];

const columnsGisLines = (onSelectAll, selectAll, project, panelId, reloadData) => [
    {
        Header: <CheckboxHeader selectAll={selectAll} onClick={() => onSelectAll(!selectAll)} />,
        Cell: (cellProps) => <CheckboxColumn {...cellProps} />,
        minWidth: 120,
        sortable: false,
    },
    {
        Header: <FormattedMessage id="name" defaultMessage="Name" />,
        accessor: 'to_str',
        minWidth: 120,
    },
    {
        Header: <FormattedMessage id="building-status" defaultMessage="Building status" />,
        accessor: 'building_status',
        minWidth: 120,
    },
    {
        Header: <FormattedMessage id="stage" defaultMessage="Stage" />,
        accessor: 'project_stage.name',
        minWidth: 120,
    },
    {
        actions: [
            {
                label: <FormattedMessage
                    id="projmanag-counterparties-quickview"
                    defaultMessage="Quick View"
                />,
                type: 'quickView',
                accessor: 'uuid',
                resourceArguments: [project.uuid],
                component: MapObjectsSidePanel,
                extraProps: gisLinesSidePanelExtraProps,
                resourceName: 'projectGisLinesNested',
                icon: <QuickViewIcon />,
                to: '/{id}/quick_view',
                getTitle: (obj) => obj.ref || obj.sequence,
                panelId,
                reload: reloadData,
            }],
        Cell: ActionsColumn,
        minWidth: 85,
    },
];

type Props = {
    intl: IntlShape,
    history: History,
    location: Location,
    project: Object,
};

const ProjectMapObjects = (props: Props) => {
    const { history, location, project } = props;

    const { formatMessage } = useIntl();

    const breadcrumbs = {
        lines: () => ({
            title: formatMessage(messages.line),
            link: `/projects/${project.uuid}/dashboard/gis-lines`,
        }),
        points: () => ({
            title: formatMessage(messages.point),
            link: `/projects/${project.uuid}/dashboard/gis-points`,
        }),
    };

    const pages = {
        gisPoints: {
            label: <FormattedMessage id="gis-points-project" defaultMessage="Gis Points" />,
            path: `/projects/${project.uuid}/dashboard/gis-points`,
        },
        gisLines: {
            label: <FormattedMessage id="gis-lines-project" defaultMessage="Gis lines" />,
            path: `/projects/${project.uuid}/dashboard/gis-lines`,
        },
    };

    const [selectedLabel, setSelectedLabel] = useState(null);

    const redirect = useCallback((path) => {
        history.push(path);
    }, [history]);

    useEffect(() => {
        const pageKey = Object.keys(pages).find((key) => pages[key].path === location.pathname);
        if (pageKey) {
            setSelectedLabel(pages[pageKey].label);
        }
    }, [location.pathname]);

    const getChoices = useCallback(() => Object.values(pages)
        .filter((page) => !selectedLabel || page.label !== selectedLabel)
        .map((page) => ({
            label: page.label,
            onClick: () => {
                redirect(page.path);
                setSelectedLabel(page.label);
            },
        })), [redirect, selectedLabel, pages]);

    const headerChildren = <PageMainSelector label={selectedLabel} choices={getChoices()} />;

    return (
        <CheckboxContextProvider>
            <Switch>
                <Route
                    path="/projects/:uuid/dashboard/gis-lines"
                    render={(routeProps) => (
                        <MapObjectsPage
                            project={project}
                            headerChildren={headerChildren}
                            messages={messages}
                            excludeFields={excludeFields.gisLines}
                            resourceName={resourceName.gisLines}
                            permissions={permissions.gisLines}
                            tableId={tableId.gisLines}
                            columns={columnsGisLines}
                            panelId={GISLINES_SIDEPANEL_KEY}
                            getObjectType={getObjectType}
                            breadcrumb={breadcrumbs.lines}
                            {...routeProps}
                        />
                    )}
                />
                <Route
                    path="/projects/:uuid/dashboard/gis-points"
                    render={(routeProps) => (
                        <MapObjectsPage
                            project={project}
                            headerChildren={headerChildren}
                            messages={messages}
                            excludeFields={excludeFields.gisPoints}
                            resourceName={resourceName.gisPoints}
                            permissions={permissions.gisPoints}
                            tableId={tableId.gisPoints}
                            columns={columnsGisPoints}
                            panelId={GISPOINTS_SIDEPANEL_KEY}
                            getObjectType={getObjectType}
                            breadcrumb={breadcrumbs.points}
                            {...routeProps}
                        />
                    )}
                />
            </Switch>
        </CheckboxContextProvider>
    );
};

export default withRouter(injectIntl(ProjectMapObjects));
